import { useCallback, useEffect, useState } from "react";

import { DeviceOrientation, PanelMode } from "./types";

// Phone detection threshold - phones have their shorter dimension <= this value.
// 448 covers iPhone 16 Pro Max (440) and Pixel 8 Pro / 9 Pro XL (~448).
const PHONE_MAX_SHORT_DIMENSION = 448;

/**
 * Calculate panel mode based on device type.
 *
 * - Phones (shorter dimension <= 448px): Always use Drawers
 * - Tablets/Desktop: Use SidePanels
 */
function calculatePanelMode(): PanelMode {
    const width = document.documentElement.clientWidth;
    const height = document.documentElement.clientHeight;

    // The shorter dimension determines if it's a phone
    const shortDimension = Math.min(width, height);

    // Phones always use Drawers, tablets/desktop use SidePanels
    return shortDimension <= PHONE_MAX_SHORT_DIMENSION ? "drawer" : "sidePanel";
}

/**
 * Calculate device orientation
 */
function calculateOrientation(): DeviceOrientation {
    const width = document.documentElement.clientWidth;
    const height = document.documentElement.clientHeight;
    return width >= height ? "landscape" : "portrait";
}

/**
 * Hook to get the current panel mode based on screen aspect ratio.
 */
export function usePanelMode(): PanelMode {
    const [mode, setMode] = useState<PanelMode>(calculatePanelMode);

    useEffect(() => {
        let prevWidth = document.documentElement.clientWidth;
        const handleResize = () => {
            const newWidth = document.documentElement.clientWidth;
            // Ignore height-only changes (e.g. iOS software keyboard appearing)
            // so the panel mode doesn't flip when the keyboard shows/hides.
            if (newWidth === prevWidth) {
                return;
            }
            prevWidth = newWidth;
            setMode(calculatePanelMode());
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return mode;
}

/**
 * Hook to get the current device orientation.
 */
export function useDeviceOrientation(): DeviceOrientation {
    const [orientation, setOrientation] = useState<DeviceOrientation>(calculateOrientation);

    useEffect(() => {
        const handleResize = () => {
            setOrientation(calculateOrientation());
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return orientation;
}

/**
 * Hook to check if we're in drawer mode (phone-like aspect ratio)
 */
export function useIsDrawerMode(): boolean {
    const mode = usePanelMode();
    return mode === "drawer";
}

/**
 * Hook to check if we're on a phone (based on screen size)
 */
export function useIsPhone(): boolean {
    const [isPhone, setIsPhone] = useState<boolean>(() => {
        const shortDimension = Math.min(document.documentElement.clientWidth, document.documentElement.clientHeight);
        return shortDimension <= PHONE_MAX_SHORT_DIMENSION;
    });

    useEffect(() => {
        const handleResize = () => {
            const shortDimension = Math.min(
                document.documentElement.clientWidth,
                document.documentElement.clientHeight,
            );
            setIsPhone(shortDimension <= PHONE_MAX_SHORT_DIMENSION);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return isPhone;
}

/**
 * Hook to check if we're on a phone in portrait mode
 */
export function useIsPhonePortrait(): boolean {
    const isPhone = useIsPhone();
    const orientation = useDeviceOrientation();
    return isPhone && orientation === "portrait";
}

/**
 * Hook to check if we're in side panel mode (tablet-like aspect ratio)
 */
export function useIsSidePanelMode(): boolean {
    const mode = usePanelMode();
    return mode === "sidePanel";
}

/**
 * Hook that returns the appropriate toggle function based on panel mode.
 * This allows toolbars to automatically invoke the correct panel type.
 */
export function usePanelToggle() {
    const mode = usePanelMode();

    const toggle = useCallback(
        (panelId: string, toggleSidePanel: (id: string) => void, toggleDrawer: (id: string) => void) => {
            if (mode === "sidePanel") {
                toggleSidePanel(panelId);
            } else {
                toggleDrawer(panelId);
            }
        },
        [mode],
    );

    return { mode, toggle };
}
